import { cn } from '@udecode/cn';
import type { Value } from '@udecode/plate';
import { Plate, useEditorPlugin, useStoreValue } from '@udecode/plate/react';
import { CommentsPlugin } from '@udecode/plate-comments/react';
import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    format,
} from 'date-fns';
import {
    CheckIcon,
    MoreHorizontalIcon,
    PencilIcon,
    TrashIcon,
    XIcon,
} from 'lucide-react';
import React, { useState } from 'react';

import { useDocumentEditor } from '../../hooks/useDocumentEditor.hook';
import { Avatar, AvatarFallback, AvatarImage } from './avatar.component';
import {
    discussionStore
} from './blockDiscussion.component';
import { Button } from './button.component';
import { useCommentEditor } from './commentCreateForm.component';
import { CommentsEditor, CommentsEditorContainer } from './commentsEditor.component';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './dropdownMenu.component';

export const formatCommentDate = (date: Date) => {
    const now = new Date();
    const diffMinutes = differenceInMinutes(now, date);
    const diffHours = differenceInHours(now, date);
    const diffDays = differenceInDays(now, date);

    if (diffMinutes < 60) {
        return `${diffMinutes}m`;
    }
    if (diffHours < 24) {
        return `${diffHours}h`;
    }
    if (diffDays < 2) {
        return `${diffDays}d`;
    }

    return format(date, 'dd. MM. yyyy');
};

export interface TComment {
  id: string;
  contentRich: Value;
  createdAt: Date;
  discussionId: string;
  isEdited: boolean;
  userId: string;
  userName: string;
  userColor: string;
  userAvatarUrl?: string;
}

export function Comment(props: {
  comment: TComment;
  discussionLength: number;
  editingId: string | null;
  index: number;
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  documentContent?: string;
  showDocumentContent?: boolean;
  onEditorClick?: () => void;
}) {
    const {
        comment,
        discussionLength,
        documentContent,
        editingId,
        index,
        setEditingId,
        showDocumentContent = false,
        onEditorClick,
    } = props;
    // const { user } = comment;

    const { cursorData } = useDocumentEditor();

    const discussions = useStoreValue(discussionStore, 'discussions');

    const resolveDiscussion = async (id: string) => {
        const updatedDiscussions = discussions.map((discussion) => {
            if (discussion.id === id) {
                return { ...discussion, isResolved: true };
            }
            return discussion;
        });
        discussionStore.set('discussions', updatedDiscussions);
    };

    const removeDiscussion = async (id: string) => {
        const updatedDiscussions = discussions.filter(
            (discussion: any) => discussion.id !== id
        );
        discussionStore.set('discussions', updatedDiscussions);
    };

    const updateComment = async (input: {
    id: string;
    contentRich: any;
    discussionId: string;
    isEdited: boolean;
  }) => {
        const updatedDiscussions = discussions.map((discussion) => {
            if (discussion.id === input.discussionId) {
                const updatedComments = discussion.comments.map((comment) => {
                    if (comment.id === input.id) {
                        return {
                            ...comment,
                            contentRich: input.contentRich,
                            isEdited: true,
                            updatedAt: new Date(),
                        };
                    }
                    return comment;
                });
                return { ...discussion, comments: updatedComments };
            }
            return discussion;
        });
        discussionStore.set('discussions', updatedDiscussions);
    };

    const { tf } = useEditorPlugin(CommentsPlugin);

    const isMyComment = cursorData.identityId === comment.userId;

    const initialValue = comment.contentRich;

    const commentEditor = useCommentEditor(
        {
            id: comment.id,
            value: initialValue,
        },
        [initialValue]
    );

    const onCancel = () => {
        setEditingId(null);
        commentEditor.tf.replaceNodes(initialValue, {
            at: [],
            children: true,
        });
    };

    const onSave = () => {
        void updateComment({
            id: comment.id,
            contentRich: commentEditor.children,
            discussionId: comment.discussionId,
            isEdited: true,
        });
        setEditingId(null);
    };

    const onResolveComment = () => {
        void resolveDiscussion(comment.discussionId);
        tf.comment.unsetMark({ id: comment.discussionId });
    };

    const isFirst = index === 0;
    const isLast = index === discussionLength - 1;
    const isEditing = editingId && editingId === comment.id;

    const [hovering, setHovering] = useState(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
        <div
            onMouseEnter={() => setHovering(true)}
            onMouseLeave={() => setHovering(false)}
        >
            <div className="relative flex items-center">
                <Avatar className="size-6">
                    <AvatarImage alt={comment.userName} src={comment.userAvatarUrl} />
                    <AvatarFallback>{comment.userName}</AvatarFallback>
                </Avatar>
                <h4 className="mx-2 text-sm leading-none font-semibold">
                    {comment.userName}
                </h4>

                <div className="text-xs leading-none text-muted-foreground/80">
                    <span className="mr-1">
                        {formatCommentDate(new Date(comment.createdAt))}
                    </span>
                    {comment.isEdited && <span>(edited)</span>}
                </div>

                {isMyComment && (hovering || dropdownOpen) && (
                    <div className="absolute top-0 right-0 flex space-x-1">
                        {index === 0 && (
                            <Button
                                variant="ghost"
                                className="h-6 p-1 text-muted-foreground"
                                onClick={onResolveComment}
                                type="button"
                            >
                                <CheckIcon className="size-4" />
                            </Button>
                        )}

                        <CommentMoreDropdown
                            onCloseAutoFocus={() => {
                                setTimeout(() => {
                                    commentEditor.tf.focus({ edge: 'endEditor' });
                                }, 0);
                            }}
                            onRemoveComment={() => {
                                if (discussionLength === 1) {
                                    tf.comment.unsetMark({ id: comment.discussionId });
                                    void removeDiscussion(comment.discussionId);
                                }
                            }}
                            comment={comment}
                            dropdownOpen={dropdownOpen}
                            setDropdownOpen={setDropdownOpen}
                            setEditingId={setEditingId}
                        />
                    </div>
                )}
            </div>

            {isFirst && showDocumentContent && (
                <div className="text-subtle-foreground relative mt-1 flex pl-[32px] text-sm">
                    {discussionLength > 1 && (
                        <div className="absolute top-[5px] left-3 h-full w-0.5 shrink-0 bg-muted" />
                    )}
                    <div className="my-px w-0.5 shrink-0 bg-highlight" />
                    {documentContent && <div className="ml-2">{documentContent}</div>}
                </div>
            )}

            <div className="relative my-1 pl-[26px]">
                {!isLast && (
                    <div className="absolute top-0 left-3 h-full w-0.5 shrink-0 bg-muted" />
                )}
                <Plate readOnly={!isEditing} editor={commentEditor}>
                    <CommentsEditorContainer variant="comment">
                        <CommentsEditor
                            variant="comment"
                            className="w-auto grow"
                            onClick={() => onEditorClick?.()}
                        />

                        {isEditing && (
                            <div className="ml-auto flex shrink-0 gap-1">
                                <Button
                                    size="icon"
                                    variant="ghost"
                                    className="size-[28px]"
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.stopPropagation();
                                        void onCancel();
                                    }}
                                >
                                    <div className="flex size-5 shrink-0 items-center justify-center rounded-[50%] bg-primary/40">
                                        <XIcon className="size-3 stroke-[3px] text-background" />
                                    </div>
                                </Button>

                                <Button
                                    size="icon"
                                    variant="ghost"
                                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        e.stopPropagation();
                                        void onSave();
                                    }}
                                >
                                    <div className="flex size-5 shrink-0 items-center justify-center rounded-[50%] bg-brand">
                                        <CheckIcon className="size-3 stroke-[3px] text-background" />
                                    </div>
                                </Button>
                            </div>
                        )}
                    </CommentsEditorContainer>
                </Plate>
            </div>
        </div>
    );
}
interface CommentMoreDropdownProps {
  comment: TComment;
  dropdownOpen: boolean;
  setDropdownOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setEditingId: React.Dispatch<React.SetStateAction<string | null>>;
  onCloseAutoFocus?: () => void;
  onRemoveComment?: () => void;
}

export function CommentMoreDropdown(props: CommentMoreDropdownProps) {
    const {
        comment,
        dropdownOpen,
        setDropdownOpen,
        setEditingId,
        onCloseAutoFocus,
        onRemoveComment,
    } = props;

    const discussions = useStoreValue(discussionStore, 'discussions');

    const selectedEditCommentRef = React.useRef<boolean>(false);

    const onDeleteComment = React.useCallback(() => {
        if (!comment.id)
            return alert('You are operating too quickly, please try again later.');

        // Find and update the discussion
        const updatedDiscussions = discussions.map((discussion: any) => {
            if (discussion.id !== comment.discussionId) {
                return discussion;
            }

            const commentIndex = discussion.comments.findIndex(
                (c: any) => c.id === comment.id
            );
            if (commentIndex === -1) {
                return discussion;
            }

            return {
                ...discussion,
                comments: [
                    ...discussion.comments.slice(0, commentIndex),
                    ...discussion.comments.slice(commentIndex + 1),
                ],
            };
        });

        // Save back to session storage
        discussionStore.set('discussions', updatedDiscussions);
        onRemoveComment?.();
    }, [comment.discussionId, comment.id, discussions, onRemoveComment]);

    const onEditComment = React.useCallback(() => {
        selectedEditCommentRef.current = true;

        if (!comment.id)
            return alert('You are operating too quickly, please try again later.');

        setEditingId(comment.id);
    }, [comment.id, setEditingId]);

    return (
        <DropdownMenu
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
            modal={false}
        >
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" className={cn('h-6 p-1 text-muted-foreground')}>
                    <MoreHorizontalIcon className="size-4" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
                className="w-48"
                onCloseAutoFocus={(e) => {
                    if (selectedEditCommentRef.current) {
                        onCloseAutoFocus?.();
                        selectedEditCommentRef.current = false;
                    }

                    return e.preventDefault();
                }}
            >
                <DropdownMenuGroup>
                    <DropdownMenuItem onClick={onEditComment}>
                        <PencilIcon className="size-4" />
            Edit comment
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onDeleteComment}>
                        <TrashIcon className="size-4" />
            Delete comment
                    </DropdownMenuItem>
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
